import { Selector } from 'react-redux';
import { RootState } from '../../redux/root-reducer';
import { IUserConfiguration } from '../../redux/configuration/types';
import { createSelector } from 'reselect';
import { IGetPublishableKeyResponse, IPaymentInitialState } from '../../redux/payment/types';
import { IPaymentCard } from '../../types/global/generic.types';
import { ISubscriptionPlan, ISubscriptionPlanInitialState } from '../../redux/subscription/types';
import { isActiveCreatorThspianSubscription } from '../../utils/creatorSubscriptionFormat';
import { resolveCreatorEntitlements, type CreatorEntitlements } from '../../utils/planEntitlements';

export const userConfigurationSelector: Selector<RootState, IUserConfiguration> = createSelector(
    (state: RootState) => state.configuration,
    config => config.userSpecificConfiguration,
);

export const appGuideIndex: Selector<RootState, number> = createSelector(
    (state: RootState) => state.configuration,
    config => config.guidedTourIndex,
);

export const paymentStateSelector: Selector<RootState, IPaymentInitialState> = createSelector(
    (state: RootState) => state.payment,
    payment => payment,
);

export const userPaymentMethodsSelector: Selector<RootState, IPaymentCard[] | undefined> = createSelector(
    [paymentStateSelector],
    method => method.paymentMethods,
);

export const userPaymentLoadingSelector: Selector<RootState, boolean> = createSelector(
    [paymentStateSelector],
    method => method.loading ?? false,
);

export const publishableKeySelector: Selector<
    RootState,
    IGetPublishableKeyResponse & { publishableKeySource?: string }
> = createSelector([paymentStateSelector], payment => ({
    publishableKey: payment.publishableKey ?? '',
    isNonUs: payment.isNonUs ?? false,
    provider: payment.publishableKeySource,
    publishableKeySource: payment.publishableKeySource,
}));

export const subscriptionPlanStateSelector: Selector<RootState, ISubscriptionPlanInitialState> = createSelector(
    (state: RootState) => state.subscription,
    sub => sub,
);

export const plansSelector: Selector<RootState, ISubscriptionPlan[]> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.subscriptionPlans,
);

export const userSubscriptionSelector: Selector<RootState, ISubscriptionPlan | undefined> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.subscription,
);

export const hasActiveCreatorThspianPlanSelector: Selector<RootState, boolean> = createSelector(
    [userSubscriptionSelector],
    subscription => isActiveCreatorThspianSubscription(subscription),
);

export const creatorEffectiveEntitlementsSelector: Selector<RootState, CreatorEntitlements> = createSelector(
    [userSubscriptionSelector, plansSelector],
    (subscription, catalogPlans) => {
        if (!isActiveCreatorThspianSubscription(subscription)) {
            return resolveCreatorEntitlements(null);
        }
        const matched = catalogPlans?.find(p => {
            const codes = new Set(
                [
                    subscription?.plan_code,
                    subscription?.subscription_plan,
                    subscription?.plan?.plan_code,
                    subscription?.plan?.id != null ? String(subscription.plan.id) : null,
                ].filter(Boolean),
            );
            return codes.has(p.plan_code);
        });
        return resolveCreatorEntitlements(
            subscription?.plan_name ?? subscription?.name ?? subscription?.plan?.name ?? matched?.name,
            subscription?.entitlements ?? matched?.entitlements,
            subscription?.effective_entitlements ?? matched?.effective_entitlements,
        );
    },
);

export const hasInstagramAutomationEntitlementSelector: Selector<RootState, boolean> = createSelector(
    [creatorEffectiveEntitlementsSelector],
    e => e.instagram_automation === true,
);

export const hasCreatorAutoUploadEntitlementSelector: Selector<RootState, boolean> = createSelector(
    [creatorEffectiveEntitlementsSelector],
    e => e.auto_upload === true,
);

export const fetchingUserSubscriptionSelector: Selector<RootState, boolean> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.fetchingLoggedInUserPlan,
);

export const userSubscriptionLoadedSelector: Selector<RootState, boolean> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.loggedInUserPlanLoaded,
);

export const updatingCreatorSubscriptionSelector: Selector<RootState, boolean> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.updatingUserPlan,
);

export const subscribingSelector: Selector<RootState, boolean> = createSelector(
    [subscriptionPlanStateSelector],
    plan => plan.subscribing,
);
